import dayjs from 'dayjs';
import type { BloodTestItem, BloodTestResult } from '../api/bloodTest';

/** クリップボードコピー時の装飾オプション */
export type BloodTestClipboardFormatOptions = {
  underline: boolean;
  redText: boolean;
};

export const DEFAULT_CLIPBOARD_FORMAT_OPTIONS: BloodTestClipboardFormatOptions = {
  underline: true,
  redText: true,
};

/** 装飾タグで囲む（両方オフのときはそのまま返す） */
export function wrapClipboardContent(content: string, options: BloodTestClipboardFormatOptions): string {
  let wrapped = content;
  if (options.redText) {
    wrapped = `<font color="red">${wrapped}</font>`;
  }
  if (options.underline) {
    wrapped = `<u>${wrapped}</u>`;
  }
  return wrapped;
}

/** クリップボード出力の1セグメント（単独項目またはスラッシュ区切りのセット） */
type ClipboardSegment = {
  label: string;
  keys: string[];
};

/** クリップボード用の出力順・ラベル定義 */
const CLIPBOARD_SEGMENTS: ClipboardSegment[] = [
  { label: 'WBC/CRP', keys: ['WBC', 'CRP'] },
  { label: 'Hb/Ht', keys: ['Hb', 'Ht'] },
  { label: 'Plt', keys: ['Plt'] },
  { label: 'TP/Alb', keys: ['TP', 'Alb'] },
  { label: 'BUN/Cr', keys: ['BUN', 'Cr'] },
  { label: 'eGFR', keys: ['eGFR'] },
  { label: 'Na/K/Cl', keys: ['Na', 'K', 'Cl'] },
  { label: 'Ca', keys: ['Ca'] },
  { label: '補正Ca', keys: ['補正Ca'] },
  { label: 'UA', keys: ['UA'] },
  { label: 'T-Bil', keys: ['T-Bil'] },
  { label: 'AST/ALT', keys: ['AST', 'ALT'] },
  { label: 'γ-GT', keys: ['γ-GT'] },
  { label: 'ALP', keys: ['ALP'] },
  { label: 'LD', keys: ['LD'] },
  { label: 'CK', keys: ['CK'] },
  { label: 'LDL-C', keys: ['LDL-C'] },
  { label: 'TG', keys: ['TG'] },
  { label: 'HbA1c', keys: ['HbA1c'] },
  { label: 'TIBC/UIBC', keys: ['TIBC', 'UIBC'] },
  { label: 'Fe', keys: ['Fe'] },
  { label: 'TSAT', keys: ['TSAT'] },
  { label: 'フェリチン', keys: ['フェリチン'] },
  { label: 'NT-proBNP', keys: ['NT-proBNP'] },
  { label: 'TSH/FT4', keys: ['TSH', 'FT4'] },
  { label: 'total P1NP', keys: ['P1NP'] },
  { label: 'TRACP-5b', keys: ['TRACP-5b'] },
  { label: '25OHビタミンD', keys: ['25OH-D'] },
];

function formatCollectionDate(iso: string | null): string {
  if (!iso?.trim()) return '';
  const d = dayjs(iso.slice(0, 10));
  return d.isValid() ? d.format('YYYY/MM/DD') : iso.trim();
}

/** クリップボード用の数値文字列（不要な小数桁を除去） */
function formatNumber(n: number): string {
  if (Number.isInteger(n)) return String(n);
  return String(parseFloat(n.toFixed(3)));
}

/** 1項目の値文字列。値も raw_text も無ければ null */
function formatItemValue(item: BloodTestItem | undefined): string | null {
  if (!item) return null;

  if (item.value !== null) {
    const flag = item.abnormality === 'L' || item.abnormality === 'H' ? item.abnormality : '';
    return `${formatNumber(item.value)}${flag}`;
  }

  if (item.raw_text?.trim()) {
    const flag = item.abnormality === 'L' || item.abnormality === 'H' ? item.abnormality : '';
    return `${item.raw_text.trim()}${flag}`;
  }

  return null;
}

function formatSegment(itemByKey: Map<string, BloodTestItem>, segment: ClipboardSegment): string | null {
  const values = segment.keys.map((key) => formatItemValue(itemByKey.get(key)));
  if (values.every((v) => v === null)) return null;

  const parts = values.map((v) => v ?? '-');
  return `${segment.label} ${parts.join('/')}`;
}

function buildPlainBloodTestClipboard(result: BloodTestResult): string {
  const itemByKey = new Map(result.items.map((item) => [item.key, item]));

  const headerParts: string[] = [];
  const dateStr = formatCollectionDate(result.collection_date);
  if (dateStr) headerParts.push(dateStr);
  if (result.hospital_name?.trim()) {
    headerParts.push(`(${result.hospital_name.trim()})`);
  }

  const segments = CLIPBOARD_SEGMENTS.map((seg) => formatSegment(itemByKey, seg)).filter(
    (s): s is string => s !== null
  );

  if (headerParts.length === 0 && segments.length === 0) return '';

  const header = headerParts.join(' ');
  return header ? `${header} ${segments.join(', ')}` : segments.join(', ');
}

/** 装飾タグなしのプレーンテキスト（プレビュー用） */
export function formatBloodTestPlainText(result: BloodTestResult): string {
  return buildPlainBloodTestClipboard(result);
}

/**
 * 採血結果をカルテ貼り付け用の1行テキストに整形する。
 */
export function formatBloodTestForClipboard(
  result: BloodTestResult,
  formatOptions: BloodTestClipboardFormatOptions = DEFAULT_CLIPBOARD_FORMAT_OPTIONS
): string {
  const plain = buildPlainBloodTestClipboard(result);
  if (!plain) return '';
  return wrapClipboardContent(plain, formatOptions);
}
